import React, { Fragment, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Cookie from 'react-cookies';
import I18n from 'i18n';
import useKeydownAndClick from '@/hooks/useKeydownAndClick';
import HorizontalScrollMenu, { MenuItem as HorizontalScrollMenuItem } from '@/components/common/HorizontalScrollMenu';
import { TCITooltip } from '@/components/ui/Tooltip';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import { redirectLinks } from '../../../modules/ExternalRedirect';
import { initVideoPlayers } from '../../../modules/TCIUtils';
import styles from './LessonPlanning.module.scss';

const MenuItem = ({ text, selected }) => (
  <button
    aria-current={selected ? 'true' : undefined}
    className={`${styles.menuItem} ${selected ? styles.active : ''}`}
    type="button"
  >
    {text}
  </button>
);

MenuItem.propTypes = {
  selected: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired
};

export const Menu = (resources, selected) => (
  resources.map((resource) => {
    const resourceId = `resource-${resource.id}`;

    return (
      <MenuItem
        key={resourceId}
        selected={!!selected}
        text={resource.title}
      />
    );
  })
);

const PrintButton = ({ pdfPath }) => (
  <TCITooltip>
    <TCITooltip.Trigger asChild>
      <a
        aria-label="Print Planning"
        className={`btn btn--link-purple ${styles.planningActions}`}
        href={pdfPath}
        rel="noopener noreferrer"
        target="_blank"
      >
        <i aria-hidden className="fa fa-print" />
      </a>
    </TCITooltip.Trigger>
    <TCITooltip.Content>Print Planning</TCITooltip.Content>
  </TCITooltip>
);

PrintButton.propTypes = {
  pdfPath: PropTypes.string.isRequired,
};

const HOVER_GRACE_MS = 100;

const ExpandCollapseButton = ({ planningCollapsed, onToggle }) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [escaped, setEscaped] = useState(false);
  const closeTimerRef = useRef(null);

  const buttonText = `${planningCollapsed ? 'Expand' : 'Collapse'} Planning`;
  const iconName = planningCollapsed ? 'Expand' : 'Collapse';
  const isOpen = (hovered || focused) && !escaped;

  // onOpenChange is a no-op by design: this component fully owns open/close
  // state via the hover, focus, and explicit Escape handlers below. Radix's
  // built-in close-on-click would fire every time the user presses Enter or
  // clicks the button, closing the tooltip even while the pointer is still
  // on the trigger — we want it to persist across the toggle so sighted
  // users see the label flip from "Expand Planning" to "Collapse Planning"
  // live.
  return (
    <TCITooltip open={isOpen} onOpenChange={() => {}}>
      <TCITooltip.Trigger asChild>
        <button
          aria-label={buttonText}
          className={`btn btn--link-purple ${styles.planningActions}`}
          id="expand-btn"
          type="button"
          onBlur={() => { setFocused(false); setEscaped(false); }}
          onClick={onToggle}
          onFocus={(e) => {
            if (e.target.matches(':focus-visible')) setFocused(true);
          }}
          onKeyDown={(e) => { if (e.key === 'Escape') setEscaped(true); }}
          onMouseEnter={() => {
            clearTimeout(closeTimerRef.current);
            setHovered(true);
          }}
          onMouseLeave={() => {
            closeTimerRef.current = setTimeout(() => {
              setHovered(false);
              setEscaped(false);
            }, HOVER_GRACE_MS);
          }}
        >
          <Icon icon={iconName} size={20} />
        </button>
      </TCITooltip.Trigger>
      <TCITooltip.Content
        onEscapeKeyDown={() => setEscaped(true)}
        onPointerEnter={() => clearTimeout(closeTimerRef.current)}
        onPointerLeave={() => { setHovered(false); setEscaped(false); }}
      >
        {buttonText}
      </TCITooltip.Content>
    </TCITooltip>
  );
};

ExpandCollapseButton.propTypes = {
  onToggle: PropTypes.func.isRequired,
  planningCollapsed: PropTypes.bool.isRequired,
};

const LessonPlanning = ({ allowPrint = false, pdfPath = '', resources, displayStudentWarning = true }) => {
  const [selected, setSelected] = useState(`resource-${resources[0].id}`);
  const [hidden, setHidden] = useState(true);
  const [containerHeight, setContainerHeight] = useState(0);
  const resourcesContainer = useRef(<div />);
  const [planningCollapsed, setPlanningCollapsed] = useState(false);
  const isInitialSelection = useRef(true);

  const togglePlanningCollapsed = () => setPlanningCollapsed((prevState) => {
    Cookie.save('planningCollapsed', !prevState, { path: '/' });
    if (!prevState && !hidden) setHidden(true);
    return !prevState;
  });

  const selectedResource = resources.find(resource => `resource-${resource.id}` === selected);
  const { html, title: selectedTitle } = selectedResource;

  const recalculateHeight = () => {
    const _hidden = hidden; // Save the current hidden state
    setHidden(false); // un-hide the container to get its full height
    if (resourcesContainer.current) {
      setContainerHeight(resourcesContainer.current.offsetHeight);
    }
    setHidden(_hidden); // restore the original hidden state
    return () => {};
  };

  const seeMoreButton = () => {
    const caretIconName = hidden ? 'CaretClosed' : 'CaretOpened';
    if (containerHeight > 159) {
      return (
        <button
          className="btn btn--link-purple tw-flex tw-items-center"
          id="seeMoreButton"
          type="button"
          onClick={() => {
            if (planningCollapsed && hidden) togglePlanningCollapsed();
            setHidden(!hidden);
          }}
        >
          <Text className="tw-inline-block tw-mr-1" size="xs" variant="body" weight="semibold">
            {hidden ? I18n.t('see_more') : I18n.t('see_less')}
          </Text>
          <Icon icon={caretIconName} size={12} />
        </button>
      );
    }

    return null;
  };

  useEffect(() => {
    resourcesContainer.current = document.getElementById('resourcesContainer');
    recalculateHeight();
    if (containerHeight > 159) setHidden(true);
    setPlanningCollapsed(Cookie.load('planningCollapsed') === 'true');
  }, []);

  useEffect(() => {
    recalculateHeight();
    initVideoPlayers();
    redirectLinks(resourcesContainer.current);

    // Move focus to the panel on user selection (not initial mount) so screen-reader
    // users are taken to the newly shown content instead of it changing silently.
    if (isInitialSelection.current) {
      isInitialSelection.current = false;
    } else {
      resourcesContainer.current?.focus();
    }
  }, [selected]);

  return (
    <Fragment>
      <div className="text--right">
        {allowPrint && pdfPath && <PrintButton pdfPath={pdfPath} />}
        <ExpandCollapseButton planningCollapsed={planningCollapsed} onToggle={togglePlanningCollapsed} />
      </div>
      <div className={`${styles.border} ${planningCollapsed ? styles.planningCollapsed : ''}`}>
        <div className={styles.label}>
          <span className={styles.primary}>
            <Text className="tw-inline-block tw-bg-white" size="sm" variant="body" weight="semibold">
              {I18n.t('planning')}
            </Text>
            <div className={styles.secondary}>
              {displayStudentWarning && I18n.t('not_visible_to_students')}
            </div>
          </span>
        </div>
        <div className="tw-pl-[5px]">
          <HorizontalScrollMenu selected={selected}>
            {resources.map((resource) => {
              const resourceId = `resource-${resource.id}`;

              return (
                <HorizontalScrollMenuItem
                  key={resourceId}
                  hideArrows
                  buttonId={resourceId}
                  className={styles.menuItem}
                  id={resourceId}
                  itemId={resourceId}
                  selected={resourceId === selected}
                  text={resource.title}
                  {...useKeydownAndClick(() => setSelected(resourceId))}
                />
              );
            })}
          </HorizontalScrollMenu>
        </div>
        <div
          className={styles.resourcesContainerWrapper}
          id="resourcesContainerWrapper"
        >
          <div
            aria-label={selectedTitle}
            className={`${styles.resourcesContainer} ${hidden ? styles.hidden : ''} fr-view`}
            id="resourcesContainer"
            role="region"
            tabIndex={-1}
          >
            <div
              className={`${styles.html} allow-list-style model-resource-container`}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>
          {seeMoreButton()}
        </div>
      </div>
    </Fragment>
  );
};

LessonPlanning.propTypes = {
  allowPrint: PropTypes.bool,
  displayStudentWarning: PropTypes.bool,
  pdfPath: PropTypes.string,
  resources: PropTypes.arrayOf(PropTypes.shape({
    html: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired
  })).isRequired
};

export default LessonPlanning;
